import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import {
  Aviso,
  Botao,
  Campo,
  CampoCpf,
  CampoDeSelecao,
  CampoTelefone,
  Cartao,
  Selo,
} from '../componentes/ui';
import { LogoDeca } from '../layout/LogoDeca';
import { requisicaoPublica } from '../servicos/http';
import { ErroDaApi } from './ErroDaApi';
import { SeletorDeVinculos } from './SeletorDeVinculos';

interface LinkPublico {
  readonly linkType: string;
  readonly empresa: string;
  readonly expiresAt: string;
  readonly submissoesRestantes: number;
}

interface ResultadoRepresentante {
  readonly nome: string;
  readonly email: string;
  readonly sucesso: boolean;
  readonly mensagem: string;
}

/**
 * RN-333 a RN-341 — Os formulários que a empresa preenche por link.
 *
 * ## Por que esta rota precisava existir com este caminho exato
 *
 * O backend **já montava a URL** apontando para cá: `urlPublicaDoLink` devolve
 * `${FRONTEND_URL}/public/{tipo}/{token}`. Sem a rota, o link que o escritório
 * enviava caía no catch-all do roteador, redirecionava para `/painel`, e de lá
 * para `/entrar` — o colaborador recebia um convite e chegava numa tela de
 * login para a qual ele justamente não tem senha ainda.
 *
 * ## Dois formulários, uma casca
 *
 * `colaboradores` cadastra uma pessoa; `representantes` manda uma grade de
 * várias. O que é comum — validar o token, mostrar de que empresa é o convite,
 * dizer quantos envios restam — fica aqui.
 */
export function FormularioPublico(): JSX.Element {
  const { tipo, token } = useParams<{ tipo: string; token: string }>();

  const link = useQuery({
    queryKey: ['link-publico', tipo, token],
    queryFn: () => requisicaoPublica<LinkPublico>(`/public/${tipo ?? ''}/${token ?? ''}`),
    retry: false,
  });

  return (
    <main className="mx-auto flex w-full max-w-2xl flex-col gap-6 px-4 py-12 sm:px-8">
      {/* A marca, não o nome escrito: é a mesma imagem da barra do app. */}
      <header className="flex flex-col gap-2">
        <LogoDeca className="h-8" />
        <p className="text-sm text-conteudo-secundario">MS3 Capital · Créditos de ICMS</p>
      </header>

      {link.isPending && <p className="text-sm text-conteudo-sutil">Validando o convite…</p>}

      {link.isError && (
        <Cartao como="section" titulo="Este convite não está mais válido">
          <p className="text-sm text-conteudo-secundario">
            Ele pode ter expirado, já ter atingido o limite de envios ou estar incompleto — alguns
            programas de e-mail quebram a linha do endereço. Peça um novo a quem lhe enviou.
          </p>
        </Cartao>
      )}

      {link.data !== undefined && (
        <Cartao
          como="section"
          titulo={tipo === 'representantes' ? 'Representantes legais' : 'Cadastro de colaborador'}
          subtitulo={link.data.empresa}
          acao={
            <Selo tom={link.data.submissoesRestantes > 1 ? 'neutro' : 'atencao'} tamanho="pequeno">
              {link.data.submissoesRestantes} envio(s) restante(s)
            </Selo>
          }
        >
          {tipo === 'representantes' ? (
            <GradeDeRepresentantes token={token ?? ''} />
          ) : (
            <CadastroDeColaborador token={token ?? ''} />
          )}
        </Cartao>
      )}

      <p className="text-center text-xs text-conteudo-sutil">
        Recebeu este link por engano? Ignore esta página — nada é registrado sem o envio.
      </p>
    </main>
  );
}

/* ─── Colaborador ─────────────────────────────────────────────────────────── */

/**
 * RN-339 — a senha só é pedida para CPF que ainda não tem conta.
 *
 * Quem já tem cadastro em outra empresa não escolhe senha de novo: o envio só
 * cria o vínculo. Pedir senha nesse caso daria a impressão de que ela seria
 * trocada — e trocar senha de conta existente a partir de um formulário público
 * é tomada de conta.
 */
function CadastroDeColaborador(props: { readonly token: string }): JSX.Element {
  const [form, setForm] = useState({
    fullName: '',
    taxpayerId: '',
    emailAddress: '',
    phone: '',
    password: '',
    confirmPassword: '',
  });
  const [vinculos, setVinculos] = useState<string[]>([]);
  const [cpfExistente, setCpfExistente] = useState<boolean | null>(null);

  const definir = (campo: keyof typeof form, valor: string): void =>
    setForm((f) => ({ ...f, [campo]: valor }));

  const checarCpf = useMutation({
    mutationFn: (cpf: string) =>
      requisicaoPublica<{ existe: boolean }>(
        `/public/colaboradores/${props.token}/check-cpf/${cpf.replace(/\D/g, '')}`,
      ),
    onSuccess: (r) => setCpfExistente(r.existe),
  });

  const enviar = useMutation({
    mutationFn: () =>
      requisicaoPublica<{ contaCriada: boolean; vinculoCriado: boolean }>(
        `/public/colaboradores/${props.token}`,
        {
          metodo: 'POST',
          corpo: {
            fullName: form.fullName,
            taxpayerId: soDigitos(form.taxpayerId),
            emailAddress: form.emailAddress,
            phone: telefoneE164(form.phone),
            ...(vinculos.length === 0 ? {} : { occupation: vinculos }),
            ...(cpfExistente === true
              ? { isExistingCPF: true }
              : { password: form.password, confirmPassword: form.confirmPassword }),
          },
        },
      ),
  });

  if (enviar.isSuccess) {
    return (
      <Aviso tom="positivo" titulo="Cadastro enviado">
        {enviar.data.contaCriada
          ? 'Sua conta foi criada e você já pode entrar com o e-mail e a senha informados.'
          : 'Seu acesso já existia e agora está vinculado a esta empresa. Entre com a senha que você já usa.'}
      </Aviso>
    );
  }

  const precisaSenha = cpfExistente === false;
  const senhasBatem = form.password !== '' && form.password === form.confirmPassword;

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        enviar.mutate();
      }}
    >
      <Campo
        rotulo="Nome completo"
        required
        value={form.fullName}
        onChange={(e) => definir('fullName', e.target.value)}
      />
      <CampoCpf
        rotulo="CPF"
        required
        ajuda="Ao sair do campo verificamos se você já tem acesso à plataforma."
        value={form.taxpayerId}
        onChange={(valor) => {
          definir('taxpayerId', valor);
          setCpfExistente(null);
        }}
        onBlur={() => {
          const digitos = form.taxpayerId.replace(/\D/g, '');
          if (digitos.length === 11) checarCpf.mutate(digitos);
        }}
      />

      {cpfExistente === true && (
        <Aviso tom="informacao" titulo="Você já tem acesso">
          Este CPF já está cadastrado. O envio só vincula você a esta empresa — sua senha atual
          continua valendo.
        </Aviso>
      )}

      <Campo
        rotulo="E-mail"
        type="email"
        required
        value={form.emailAddress}
        onChange={(e) => definir('emailAddress', e.target.value)}
      />
      <CampoTelefone
        rotulo="Telefone"
        required
        ajuda="Com DDD."
        value={form.phone}
        onChange={(valor) => definir('phone', valor)}
      />
      <SeletorDeVinculos
        escolhidos={vinculos}
        aoMudar={setVinculos}
        ajuda="Pode marcar mais de um. É por ele que a empresa sabe a quem enviar cada aviso."
      />

      {precisaSenha && (
        <>
          <Campo
            rotulo="Crie uma senha"
            type="password"
            required
            autoComplete="new-password"
            ajuda="Você usa esta senha para entrar na plataforma."
            value={form.password}
            onChange={(e) => definir('password', e.target.value)}
          />
          <Campo
            rotulo="Repita a senha"
            type="password"
            required
            autoComplete="new-password"
            value={form.confirmPassword}
            onChange={(e) => definir('confirmPassword', e.target.value)}
          />
          {form.confirmPassword !== '' && !senhasBatem && (
            <p className="text-xs text-erro">As senhas não são iguais.</p>
          )}
        </>
      )}

      {enviar.isError && <ErroDaApi titulo="Não foi possível enviar" erro={enviar.error} />}

      <Botao type="submit" carregando={enviar.isPending} disabled={precisaSenha && !senhasBatem}>
        Enviar cadastro
      </Botao>
    </form>
  );
}

/* ─── Representantes ──────────────────────────────────────────────────────── */

interface LinhaDeRepresentante {
  nome: string;
  cpf: string;
  email: string;
  telefone: string;
  tipo: string;
}

const LINHA_VAZIA: LinhaDeRepresentante = {
  nome: '',
  cpf: '',
  email: '',
  telefone: '',
  tipo: '',
};

/**
 * Os cinco papéis da grade do Deca 1 (`PublicRepresentantesForm.js`). O backend
 * exige `tipo`; sem ele o envio inteiro voltava "Informe o tipo do
 * representante" — e a tela anterior não tinha como informá-lo.
 *
 * A tela manda **só o papel**. O vínculo trabalhista que ele implica ("Sócio",
 * "Contábil"…) é derivado no servidor (`links.service.ts`), que é quem grava a
 * coluna e conhece o formato dela. Quando a tela mandava a ocupação junto, o
 * servidor concatenava os dois e a coluna recebia "responsavel_contabil /
 * Contábil" — que a aba de colaboradores lia como um vínculo fora da lista.
 */
const PAPEIS_DE_REPRESENTANTE: readonly { valor: string; rotulo: string }[] = [
  { valor: 'socio_administrador_1', rotulo: 'Sócio administrador (1º representante legal)' },
  { valor: 'socio_administrador_2', rotulo: 'Sócio administrador (2º)' },
  { valor: 'focal_point', rotulo: 'Ponto focal (contato principal)' },
  { valor: 'responsavel_contabil', rotulo: 'Responsável contábil' },
  { valor: 'responsavel_financeiro', rotulo: 'Responsável financeiro' },
];

/**
 * RN-340 — a grade de quem assina pela empresa.
 *
 * Várias pessoas de uma vez porque é assim que a informação existe: o cliente
 * responde "somos três sócios" e manda os três. Um formulário por pessoa
 * significaria abrir o link três vezes — e o link tem limite de envios.
 *
 * O resultado vem **linha a linha**: uma pessoa pode falhar (CPF já usado por
 * outro nome, e-mail inválido) e as outras entrarem. Mostrar "erro" no conjunto
 * esconderia que duas das três foram cadastradas.
 */
function GradeDeRepresentantes(props: { readonly token: string }): JSX.Element {
  const [linhas, setLinhas] = useState<LinhaDeRepresentante[]>([{ ...LINHA_VAZIA }]);

  const definir = (i: number, campo: keyof LinhaDeRepresentante, valor: string): void => {
    setLinhas((atual) => atual.map((l, idx) => (idx === i ? { ...l, [campo]: valor } : l)));
  };

  const enviar = useMutation({
    mutationFn: () =>
      requisicaoPublica<{ resultados: readonly ResultadoRepresentante[] }>(
        `/public/representantes/${props.token}`,
        {
          metodo: 'POST',
          corpo: {
            representantes: linhas
              .filter((l) => l.nome.trim() !== '' || l.cpf.trim() !== '')
              .map((l) => ({
                nome: l.nome,
                cpf: soDigitos(l.cpf),
                email: l.email,
                telefone: telefoneE164(l.telefone),
                tipo: l.tipo,
              })),
          },
        },
      ),
  });

  if (enviar.isSuccess) {
    const resultados = enviar.data.resultados ?? [];
    const ok = resultados.filter((r) => r.sucesso).length;

    return (
      <div className="flex flex-col gap-3">
        <Aviso
          tom={ok === resultados.length ? 'positivo' : 'atencao'}
          titulo={`${ok} de ${resultados.length} cadastrado(s)`}
        >
          Cada linha é analisada por conta própria — uma falha não derruba as outras. Quem foi
          cadastrado recebe por e-mail as instruções de acesso.
        </Aviso>
        <ul className="flex flex-col gap-1">
          {resultados.map((r) => (
            <li key={`${r.nome}-${r.email}`} className="text-sm">
              <Selo tom={r.sucesso ? 'positivo' : 'critico'} tamanho="pequeno">
                {r.sucesso ? 'OK' : 'Falhou'}
              </Selo>{' '}
              <span className="text-conteudo-primario">{r.nome || r.email}</span>
              <span className="ml-2 text-xs text-conteudo-sutil">{r.mensagem}</span>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        enviar.mutate();
      }}
    >
      {linhas.map((linha, i) => (
        <fieldset key={i} className="flex flex-col gap-3 rounded-card border border-borda p-4">
          <legend className="px-1 text-xs uppercase tracking-wide text-conteudo-sutil">
            Representante {i + 1}
          </legend>
          <CampoDeSelecao
            rotulo="Papel na empresa"
            required
            placeholder="Selecione"
            value={linha.tipo}
            onChange={(e) => definir(i, 'tipo', e.target.value)}
            opcoes={PAPEIS_DE_REPRESENTANTE.map((p) => ({ valor: p.valor, rotulo: p.rotulo }))}
          />
          <Campo
            rotulo="Nome completo"
            value={linha.nome}
            onChange={(e) => definir(i, 'nome', e.target.value)}
          />
          <div className="grid gap-3 sm:grid-cols-2">
            <CampoCpf
              rotulo="CPF"
              value={linha.cpf}
              onChange={(valor) => definir(i, 'cpf', valor)}
            />
            <CampoTelefone
              rotulo="Telefone"
              value={linha.telefone}
              onChange={(valor) => definir(i, 'telefone', valor)}
            />
          </div>
          <Campo
            rotulo="E-mail"
            type="email"
            value={linha.email}
            onChange={(e) => definir(i, 'email', e.target.value)}
          />
          {linhas.length > 1 && (
            <div>
              <Botao
                type="button"
                variante="sutil"
                tamanho="pequeno"
                onClick={() => setLinhas((atual) => atual.filter((_, idx) => idx !== i))}
              >
                Remover
              </Botao>
            </div>
          )}
        </fieldset>
      ))}

      {enviar.isError && <ErroDaApi titulo="Não foi possível enviar" erro={enviar.error} />}

      <div className="flex flex-wrap gap-3">
        <Botao
          type="button"
          variante="secundario"
          onClick={() => setLinhas((atual) => [...atual, { ...LINHA_VAZIA }])}
        >
          Adicionar representante
        </Botao>
        <Botao type="submit" carregando={enviar.isPending}>
          Enviar {linhas.length > 1 ? `os ${linhas.length}` : ''}
        </Botao>
      </div>
    </form>
  );
}

/* ─── Normalização ────────────────────────────────────────────────────────── */

function soDigitos(valor: string): string {
  return valor.replace(/\D/g, '');
}

/**
 * O backend exige `+55` + DDD + número (E.164 brasileiro). Quem preenche digita
 * "(43) 99999-8888" — o Deca 1 já tirava a máscara e prefixava o `+55` antes de
 * enviar; sem isso todo envio voltava com erro no telefone.
 */
function telefoneE164(valor: string): string {
  const digitos = soDigitos(valor).replace(/^55(?=\d{10,11}$)/, '');
  return digitos === '' ? '' : `+55${digitos}`;
}
